import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import { initScrollAnimations, cleanupScrollAnimations } from '../utils/scrollAnimations';
import '../styles/thevenue.css';
import '../styles/animations.css';

const TheVenue = () => {
  const { language } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize scroll animations
  useEffect(() => {
    const scrollObserver = initScrollAnimations();
    return () => {
      cleanupScrollAnimations(scrollObserver);
    };
  }, []);

  // Ensure video autoplay
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(error => {
        console.log('Autoplay failed:', error);
      });
    }
  }, []);

  return (
    <div className="the-venue-page">
      <Header isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section className="the-venue-hero">
        <video 
          ref={videoRef}
          className="the-venue-hero-video"
          autoPlay 
          loop 
          muted 
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
          defaultMuted
          controls={false}
        >
          <source src="/assets/PawilonDronFlyOver.MP4" type="video/mp4" />
        </video>
        <div className="the-venue-hero-overlay"></div>
        <div className="the-venue-hero-content">
          <h1 className="the-venue-hero-title">{t('venue.hero.title', language)}</h1>
          <p className="the-venue-hero-subtitle">{t('venue.hero.subtitle', language)}</p>
        </div>
      </section>

      {/* Main Venue Section - 50/50 */}
      <section className="the-venue-main-hero scroll-animate">
        <div className="the-venue-main-hero-content">
          <div className="the-venue-main-hero-left scroll-animate delay-100">
            <img src="/assets/BuildingOutStreet.jpeg" alt="Main Hall" className="the-venue-main-hero-image" />
          </div>
          <div className="the-venue-main-hero-right scroll-animate delay-200">
            <div className="the-venue-main-hero-info">
              <h2 className="the-venue-main-hero-title">{t('venue.main.title', language)}</h2>
              <p className="the-venue-main-hero-description">
                {t('venue.main.description1', language)}
              </p>
              <p className="the-venue-main-hero-description">
                {t('venue.main.description2', language)}
              </p>
              <div className="the-venue-main-hero-buttons">
                <a href="/private-events" className="the-venue-main-hero-button">{t('venue.main.bookEvent', language)}</a>
                <a href="/events" className="the-venue-main-hero-link">{t('venue.main.buyTickets', language)}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Venue Spaces Section */}
      <section className="the-venue-spaces">
        <div className="the-venue-spaces-content">
          <h2 className="the-venue-spaces-title">{t('venue.spaces.title', language)} {t('venue.spaces.subtitle', language)}</h2>
          
          <div className="the-venue-spaces-grid">
            {/* Main Hall */}
            <div className="the-venue-space-card">
              <div className="the-venue-space-image">
                <img src="/assets/MainHallInterior.png" alt="Main Hall" />
              </div>
              <div className="the-venue-space-content">
                <h3 className="the-venue-space-title">{t('venue.spaces.mainHall', language)}</h3>
                <p className="the-venue-space-capacity">{language === 'pl' ? 'Centrum obiektu, z miejscami siedzącymi dla maksymalnie 40 gości' : 'The centerpiece of the venue, with seating for up to 60 guests'}</p>
                <p className="the-venue-space-description">
                  {language === 'pl' ? 'Elastyczne aranżacje sprawiają, że nadaje się zarówno na formalne kolacje, jak i przyjęcia w otwartej przestrzeni. Posiada wysokie sufity i oryginalne detale architektoniczne.' : 'Flexible arrangements make it suitable for both formal dinners and open-floor receptions. Features high ceilings and original architectural details.'}
                </p>
              </div>
            </div>


            {/* Rooftop Bar */}
            <div className="the-venue-space-card">
              <div className="the-venue-space-image">
                <img src="/assets/RoofTopWTables.jpeg" alt="Rooftop Bar" />
              </div>
              <div className="the-venue-space-content">
                <h3 className="the-venue-space-title">{t('venue.spaces.rooftop', language)}</h3>
                <p className="the-venue-space-capacity">{language === 'pl' ? 'Miejsce na świeżym powietrzu idealne na muzykę, taniec i letnie wieczory' : 'An open-air setting perfect for music, dancing, and summer evenings'}</p>
                <p className="the-venue-space-description">
                  {language === 'pl' ? 'Taras zewnętrzny z przepięknymi widokami na miasto, idealny na przyjęcia koktajlowe, wydarzenia networkingowe i letnie uroczystości.' : 'An outdoor terrace with stunning city views, perfect for cocktail receptions, networking events, and summer celebrations.'}
                </p>
              </div>
            </div>
            
            {/* Wine Cellar Banquet Hall */}
            <div className="the-venue-space-card">
              <div className="the-venue-space-image">
                <img src="/assets/Garbary7.jpg" alt="Wine Cellar Banquet Hall" />
              </div>
              <div className="the-venue-space-content">
                <h3 className="the-venue-space-title">{language === 'pl' ? 'Sala Piwniczna Winna' : 'Wine Cellar Hall'}</h3>
                <p className="the-venue-space-capacity">{language === 'pl' ? 'Wkrótce - Charakterystyczna przestrzeń podziemna' : 'Coming soon - A distinctive underground space'}</p>
                <p className="the-venue-space-description">
                  {language === 'pl' ? 'Zaprojektowana do pomieszczenia 60–80 gości, dodająca głębi i różnorodności do obiektu. Unikalne podziemne miejsce idealne na kameralne kolacje i specjalne uroczystości.' : 'Designed to accommodate 60–80 guests, adding depth and variety to the venue. A unique underground setting perfect for intimate dinners and special celebrations.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Venue Information Section */}
      <section className="venue-info-section">
        <div className="venue-info-container">
          
          {/* Main Content Grid */}
          <div className="venue-main-grid">
            {/* Left Column */}
            <div className="venue-left-column">
              <h2 className="venue-section-title">{language === 'pl' ? 'Atmosfera i Układ' : 'Atmosphere & Setup'}</h2>
              <p className="venue-section-description">
                {language === 'pl' ? 'Każda przestrzeń w Pawilonie może być dostosowana do tonu okazji. Niezależnie od tego, czy ustawiona z eleganckimi nakryciami stołu, czy pozostawiona otwarta na żywą uroczystość, wnętrza są zaprojektowane tak, aby być wszechstronnymi. Dekoracja, oświetlenie i układ można dostosować do stylu wydarzenia.' : 'Every space at Pawilon can be adapted to match the tone of the occasion. Whether arranged with elegant table settings or left open for a lively celebration, the interiors are designed to be versatile. Decoration, lighting, and layout can all be tailored to the style of the event.'}
              </p>
              
              <div className="venue-address-info">
                <h3 className="venue-subsection-title">{language === 'pl' ? 'Adres' : 'Address'}</h3>
                <p className="venue-address-text">
                  Pawilon Krzyżanowskiego<br />
                  Ul. Garbary 96<br />
                  61-757 Poznań, {language === 'pl' ? 'Polska' : 'Poland'}
                </p>
              </div>

              <div className="venue-accessibility-info">
                <h3 className="venue-subsection-title">{language === 'pl' ? 'Dostępność i Lokalizacja' : 'Accessibility & Location'}</h3>
                <p className="venue-section-description">
                  {language === 'pl' ? 'Obiekt znajduje się w odległości spaceru od kulturalnego i historycznego centrum Poznania, blisko linii transportu publicznego. Chociaż nie oferujemy parkingu na miejscu, centralna lokalizacja zapewnia łatwy dostęp dla gości przyjeżdżających z całego miasta.' : 'The venue is within walking distance of Poznań\'s cultural and historic center, close to public transport lines. While we do not offer on-site parking, the central location ensures easy access for guests arriving from across the city.'}
                </p>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="venue-right-column">
              <div className="venue-map-wrapper">
                <iframe
                  src="https://www.openstreetmap.org/export/embed.html?bbox=16.9360%2C52.4100%2C16.9420%2C52.4140&amp;layer=mapnik&amp;marker=52.4121,16.9389"
                  width="100%"
                  height="560"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Pawilon Krzyżanowskiego Location Map"
                />
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* CTA Section */}
      <section className="the-venue-cta">
        <div className="the-venue-cta-content">
          <h2 className="the-venue-cta-title">{language === 'pl' ? 'Gotowy Doświadczyć Naszego Obiektu?' : 'Ready to Experience Our Venue?'}</h2>
          <p className="the-venue-cta-description">
            {language === 'pl' ? 'Umów prywatną wizytę, aby zobaczyć nasze przestrzenie osobiście i omówić potrzeby swojego wydarzenia.' : 'Schedule a private tour to see our spaces in person and discuss your event needs.'}
          </p>
          <div className="the-venue-cta-buttons">
            <a href="/private-events" className="the-venue-cta-button">{t('venue.main.bookEvent', language)}</a>
            <a href="/events" className="the-venue-cta-link">{language === 'pl' ? 'Kup Bilet' : 'Buy A Ticket'}</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TheVenue;